import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import {
	CreateCustomFieldData,
	Location,
	locationEnum as CustomFieldLocation,
	Type,
	typeEnum as CustomFieldType,
} from '@jobilla/nexus-sdk'
import type { AxiosError } from 'axios'
import { t } from 'i18next'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { Button, SelectField, TextField } from '../../../../rapid-ui'

interface CreateCustomFieldDialogProps {
	open: boolean
	onClose: () => void
	onSubmit: (data: CreateCustomFieldData) => void
	isLoading: boolean
	error: AxiosError<{ message?: string }> | null
}

export function CreateCustomFieldDialog({
	open,
	onClose,
	onSubmit,
	isLoading,
	error,
}: CreateCustomFieldDialogProps) {
	const IsRequiredOptions = {
		required: { value: 'required', label: t('settings.customFields.required') },
		optional: { value: 'optional', label: t('settings.customFields.optional') },
	}

	const Locations = {
		[CustomFieldLocation.invoice]: {
			value: CustomFieldLocation.invoice,
			label: t('settings.customFields.locations.invoice'),
		},
	}

	const Types = {
		[CustomFieldType.text]: { value: CustomFieldType.text, label: t('settings.customFields.types.text') },
	}

	const [name, setName] = useState('')
	const [location, setLocation] = useState<Location>(Locations[CustomFieldLocation.invoice].value)
	const [type, setType] = useState<Type>(Types[CustomFieldType.text].value)
	const [isRequired, setIsRequired] = useState(false)

	useEffect(() => {
		setName('')
		setLocation(Locations[CustomFieldLocation.invoice].value)
		setType(Types[CustomFieldType.text].value)
		setIsRequired(false)
	}, [open])

	const handleSubmit = () => {
		if (!name) { return }

		onSubmit({
			name,
			type,
			location,
			isRequired,
		})
	}

	const isValid = name !== null && name.trim() !== ''

	return (
		<AnimatePresence>
			{open && (
				<Dialog static open={open} onClose={onClose} className="relative z-50">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/30 backdrop-blur-sm"
					/>

					<div className="fixed inset-0 flex items-center justify-center p-4">
						<DialogPanel
							as={motion.div}
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
							className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden"
						>
							<div className="p-6">
								<DialogTitle className="text-lg font-semibold text-gray-900">
									{t('settings.customFields.add')}
								</DialogTitle>

								<div className="mt-4 space-y-4">
									<TextField
										label={t('settings.customFields.name')}
										value={name}
										onChange={setName}
										placeholder={t('settings.customFields.nameHint')}
									/>
								</div>

								<div className="mt-4 space-y-4">
									<SelectField
										label={t('settings.customFields.location')}
										value={Locations[location]}
										onChange={(value) => {
											setLocation(value.value)
										}}
										options={Object.values(Locations)}
										renderOption={(option) => option.label}
									/>
								</div>

								<div className="mt-4 space-y-4">
									<SelectField
										label={t('settings.customFields.type')}
										value={Types[type]}
										onChange={(value) => {
											setType(value.value)
										}}
										options={Object.values(Types)}
										renderOption={(option) => option.label}
									/>
								</div>

								<div className="mt-4 space-y-4">
									<SelectField
										label={t('settings.customFields.isRequired')}
										value={isRequired ? IsRequiredOptions.required : IsRequiredOptions.optional}
										onChange={(value) => {
											setIsRequired(value.value === 'required')
										}}
										options={Object.values(IsRequiredOptions)}
										renderOption={(option) => option.label}
									/>
								</div>

								<div className="mt-4 space-y-4">
									{error && (
										<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
											<div className="font-medium text-red-900">
												{t('settings.customFields.create.failed')}
											</div>
											<div className="text-red-700 mt-0.5">
												{error?.response?.data?.message || error.message
													|| t('common.tryAgain')}
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
								<Button type="subtle" onClick={onClose} disabled={isLoading}>
									{t('common.cancel')}
								</Button>

								<Button
									type="committal"
									onClick={handleSubmit}
									isLoading={isLoading}
									disabled={!isValid}
								>
									{isLoading ? t('common.creating') : t('common.save')}
								</Button>
							</div>
						</DialogPanel>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}
